import { FbConversation } from '@/types/fb-type';
import { Search, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConversationList } from './ConversationList';

interface ChatListProp {
  conversations?: FbConversation[];
  loadingConversations: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  currentConversation?: FbConversation | null;
  onSelectConversation: (conversation: FbConversation) => void;
  onLoadMore?: () => void;
  refreshSignal?: { convId?: string; ts: number };
}

export default function ChatList({
  onSelectConversation,
  currentConversation,
  conversations = [],
  loadingConversations,
  isLoadingMore = false,
  hasMore = false,
  onLoadMore,
}: ChatListProp) {
  const { t } = useTranslation('chat');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = searchQuery.trim()
    ? conversations.filter((c) => {
        const name = c.getUser()?.name?.toLowerCase() ?? '';
        const snippet = (c.snippet ?? '').toLowerCase();
        const q = searchQuery.toLowerCase();
        return name.includes(q) || snippet.includes(q);
      })
    : conversations;

  return (
    <div className="flex flex-col h-full min-h-0 w-full overflow-hidden">
      {/* Search bar */}
      <div className="flex-shrink-0 px-3 py-2 border-b border-gray-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          <input
            type="text"
            placeholder={t('search') || 'Search conversations…'}
            className="w-full pl-9 pr-8 py-2 rounded-full bg-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition placeholder-gray-400"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Conversation list */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <ConversationList
          items={filtered}
          activeId={currentConversation?.id ?? null}
          onSelect={onSelectConversation}
          isLoading={loadingConversations || isLoadingMore}
          onEndReached={hasMore && !isLoadingMore ? onLoadMore : undefined}
          emptyLabel={searchQuery ? t('noResults') || 'No results' : t('noConversations') || 'No conversations yet'}
        />
      </div>
    </div>
  );
}
